import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { Box, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../../hooks/useGaneshApi";
import { renderNumberCell } from "utils/dataGrid";
import { ComplianceIssueBooleanIcon } from "../UI/ComplianceIssueBooleanIcon";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

import { useError } from "../../../contexts/ErrorContext";
import type { CustomerSchemaSummary_T } from "../../../types/Schema";

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: { items: [] },
  },
  sorting: { sortModel: [{ field: "id", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
};

const SchemaComplianceSummary: React.FC<any> = () => {
  const { showError } = useError();
  const { apiRequest } = useApi();
  const navigate = useNavigate();

  const fetchSchemaComplianceSummary = async (): Promise<
    CustomerSchemaSummary_T[]
  > => {
    const response = await apiRequest<CustomerSchemaSummary_T[]>(
      `/v1/schemas/schema-compliance-summary`,
      { method: "GET" }
    );
    return response.data || [];
  };

  const {
    data: schemaComplianceSummaryData,
    isLoading,
    error,
  } = useQuery<CustomerSchemaSummary_T[], Error>({
    queryKey: ["schemaComplianceSummary"],
    queryFn: fetchSchemaComplianceSummary,
    staleTime: 120000, // 2 minutes
    refetchInterval: false,
  });

  useEffect(() => {
    if (error) {
      showError(
        `Failed to fetch schema compliance summary: ${
          error.message || "Unknown error"
        }`
      );
    }
  }, [error, showError]);

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography>Error: {error.message}</Typography>;
  }

  if (
    !schemaComplianceSummaryData ||
    schemaComplianceSummaryData.length === 0
  ) {
    return <Typography>No data available</Typography>;
  }

  const columns_def: GridColDef[] = [
    {
      field: "customer_id",
      headerName: "Customer ID",
      type: "number",
      width: 100,
      flex: 1,
    },
    {
      field: "name",
      headerName: "Name",
      type: "string",
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          to={`/customer-schemas/${params.row.customer_id}`}
          state={{ customerName: params.value }}
        >
          {params.value}
        </Link>
      ),
      flex: 1,
    },
    {
      field: "delivery_count",
      headerName: "Delivery Count",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
      flex: 1,
    },
    {
      field: "distinct_schema_count",
      headerName: "Distinct Schema Count",
      description: "Number of distinct schemas for this customer",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
      flex: 1,
    },
    {
      field: "unique_attributes",
      headerName: "Unique Attributes",
      description:
        "Number of unique attributes for this customer across all schemas",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
      flex: 1,
    },
    {
      field: "unique_labels",
      headerName: "Unique Labels",
      description:
        "Number of unique labels for this customer across all schemas",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
      flex: 1,
    },
    {
      field: "unique_meta_keys",
      headerName: "Unique Meta Keys",
      description:
        "Number of unique meta keys for this customer across all schemas",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
      flex: 1,
    },
    {
      field: "unique_meta_values",
      headerName: "Unique Meta Values",
      description:
        "Number of unique meta values for this customer across all schemas",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
      flex: 1,
    },
    {
      field: "missing_attributes",
      headerName: "Missing Attributes",
      description:
        "Number of attributes that are missing for this customer across all schemas",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
      flex: 1,
    },
    {
      field: "label_conflicts",
      headerName: "Label Conflicts",
      description:
        "Number of label conflicts for this customer across all schemas",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
      flex: 1,
    },
    {
      field: "missing_meta",
      headerName: "Missing Meta",
      description:
        "Number of meta values that are missing for this customer across all schemas",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
      flex: 1,
    },
    {
      field: "meta_conflicts",
      headerName: "Meta Conflicts",
      description:
        "Number of meta value conflicts for this customer across all schemas",
      type: "number",
      width: 100,
      renderCell: renderNumberCell,
      flex: 1,
    },
    {
      field: "inconsistent_labels",
      headerName: "Inconsistent Labels",
      description: "true = ISSUE (compliance problem), false = OK (compliant)",
      type: "boolean",
      width: 120,
      renderCell: ComplianceIssueBooleanIcon,
      flex: 1,
    },
    {
      field: "inconsistent_meta_values",
      headerName: "Inconsistent Meta Values",
      description: "true = ISSUE (compliance problem), false = OK (compliant)",
      type: "boolean",
      width: 150,
      renderCell: ComplianceIssueBooleanIcon,
      flex: 1,
    },
  ];

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Schema Compliance Summary
      </Typography>

      <GaneshDataGrid
        loading={isLoading}
        data={schemaComplianceSummaryData || []}
        columns={columns_def}
        gridId="schemaComplianceSummaryGrid"
        defaultGridState={defaultGridState}
      />
    </Box>
  );
};

export default SchemaComplianceSummary;
